import React, { useState, useEffect } from 'react';
import {
    getSparks, // Implies getting ALL sparks. 
    // Wait, getSparksFiltered(userId) gets all non-reported. I should use that or export getSparks (raw)
    getSparksFiltered,
    getFriends,
    getMagicSparkHolder,
    getTopUsers,
    BADGES
} from '../services/firestore.js';
import { Trophy, Flame, Heart, Award, Users, Sparkles, Zap, Smile, ChevronDown, ChevronUp, Crown, Lightbulb, Rocket } from 'lucide-react';

import UserBadge from '../components/UserBadge.jsx';

export default function Leaderboard({ onViewProfile, user: currentUser }) { // user prop passed from App.js
    const [topCreators, setTopCreators] = useState([]);
    const [topSparks, setTopSparks] = useState([]);
    const [topReactedSparks, setTopReactedSparks] = useState([]);
    const [friendTopCreators, setFriendTopCreators] = useState([]);
    const [friendTopSparks, setFriendTopSparks] = useState([]);
    const [friendTopReactedSparks, setFriendTopReactedSparks] = useState([]);
    const [magicSparkHolder, setMagicSparkHolder] = useState(null);
    const [loading, setLoading] = useState(true);

    // Expandable panel state
    const [expandedPanels, setExpandedPanels] = useState({});

    useEffect(() => {
        if (!currentUser?.uid) return;

        const loadData = async () => {
            setLoading(true);
            try {
                // Fetch all data
                // We need separate queries for Top Creators and Top Liked because Firestore sort is specific.
                const [creators, topLikedUsers, sparks, friends, magicSpark] = await Promise.all([
                    getTopUsers('sparksCreated', 10),
                    getTopUsers('likesReceived', 10),
                    getSparksFiltered(currentUser.uid), // Still need sparks for "Friend Top Sparks" (showing actual content) or could use user stats too?

                    getFriends(currentUser.uid),
                    getMagicSparkHolder()
                ]);

                setMagicSparkHolder(magicSpark?.holderName);

                // --- Global Creators (From User Stats) ---
                const formattedCreators = creators.map(u => ({
                    id: u.id,
                    name: u.name,
                    sparks: u.stats?.sparksCreated || 0,
                    totalLikes: u.stats?.likesReceived || 0
                }));
                setTopCreators(formattedCreators);

                // --- Global Top Sparks (From Content) ---
                // Leaderboard for individual content pieces uses actual spark documents
                const topLiked = [...sparks]
                    .sort((a, b) => (b.likes || 0) - (a.likes || 0))
                    .slice(0, 10);
                setTopSparks(topLiked);

                // --- Global Most Reacted Sparks ---
                // Calculate total reaction count for each spark
                const sparksWithReactionCounts = sparks.map(spark => {
                    let totalReactions = 0;
                    if (spark.reactions) {
                        // Sum all emoji reaction arrays
                        Object.values(spark.reactions).forEach(userIds => {
                            totalReactions += userIds.length;
                        });
                    }
                    return { ...spark, totalReactions };
                });

                const topReacted = sparksWithReactionCounts
                    .sort((a, b) => b.totalReactions - a.totalReactions)
                    .slice(0, 10);
                setTopReactedSparks(topReacted);

                // Friends logic
                const friendIds = friends.map(f => f.id);

                // Re-using spark aggregation for friends (subset) is safer than nothing for now.
                const creatorStats = {};
                sparks.forEach(spark => {
                    if (!creatorStats[spark.authorId]) {
                        creatorStats[spark.authorId] = { id: spark.authorId, name: spark.author, sparks: 0, totalLikes: 0 };
                    }
                    creatorStats[spark.authorId].sparks++;
                    creatorStats[spark.authorId].totalLikes += (spark.likes || 0);
                    if (spark.author) creatorStats[spark.authorId].name = spark.author;
                });

                const friendCreatorsCalculated = Object.values(creatorStats)
                    .filter(c => friendIds.includes(c.id))
                    .sort((a, b) => b.sparks - a.sparks)
                    .slice(0, 10);
                setFriendTopCreators(friendCreatorsCalculated);

                const friendLikedCalculated = sparks
                    .filter(s => friendIds.includes(s.authorId))
                    .sort((a, b) => (b.likes || 0) - (a.likes || 0))
                    .slice(0, 10);
                setFriendTopSparks(friendLikedCalculated);

                // --- Friends Most Reacted Sparks ---
                const friendReactedCalculated = sparksWithReactionCounts
                    .filter(s => friendIds.includes(s.authorId))
                    .sort((a, b) => b.totalReactions - a.totalReactions)
                    .slice(0, 10);
                setFriendTopReactedSparks(friendReactedCalculated);

            } catch (error) {
                console.error("Error loading leaderboard:", error);
            } finally {
                setLoading(false);
            }
        };

        loadData();
    }, [currentUser]);

    const getRankColor = (rank) => {
        if (rank === 1) return '#fbbf24'; // Gold
        if (rank === 2) return '#d1d5db'; // Silver
        if (rank === 3) return '#f97316'; // Bronze
        return '#999';
    };

    // Sync badges on load (Database is Source of Truth)
    useEffect(() => {
        if (currentUser?.uid) {
            import('../services/firestore.js').then(module => {
                module.checkAndUnlockBadges(currentUser.uid);
            });
        }
    }, [currentUser?.uid]);

    // Merge current user badges with definitions (STRICT DISPLAY - Database Only)
    const userBadges = currentUser ? BADGES.filter(badge => {
        // MYSTERY LOGIC: 'vip_gold_crown' only visible to Mega Fans
        if (badge.id === 'vip_gold_crown' && currentUser.subscriptionTier !== 'mega_fan') return false;
        return true;
    }).map(badgeDef => {
        const storedUnlock = currentUser.badges?.find(b => b.id === badgeDef.id);

        return {
            ...badgeDef,
            unlocked: storedUnlock?.unlocked || false,
            unlockedAt: storedUnlock?.unlockedAt
        };
    }) : [];

    const iconMap = {
        Zap: Zap,
        Flame: Flame,
        Sparkles: Sparkles,
        Award: Award,
        Users: Users,
        Trophy: Trophy,
        Lightbulb: Lightbulb,
        Crown: Crown,
        Rocket: Rocket
    };

    const togglePanel = (panelId) => {
        setExpandedPanels(prev => ({ ...prev, [panelId]: !prev[panelId] }));
    };

    const renderLeaderboardPanel = (title, icon, data, type = 'creators', panelId) => {
        const isExpanded = expandedPanels[panelId] || false;
        const displayData = isExpanded ? data.slice(0, 25) : data.slice(0, 3);

        // Find user's rank in this category
        const userRank = currentUser ? data.findIndex(item => {
            if (type === 'creators') {
                return item.id === currentUser.uid;
            } else {
                return item.authorId === currentUser.uid;
            }
        }) + 1 : 0;

        return React.createElement('div', { className: 'glass-panel', style: { padding: '25px' } },
            React.createElement('h2', { style: { marginBottom: '20px', display: 'flex', alignItems: 'center', gap: '10px', fontSize: '20px' } },
                icon,
                title
            ),
            React.createElement('div', { style: { display: 'flex', flexDirection: 'column', gap: '12px' } },
                displayData.length === 0 ?
                    React.createElement('div', { style: { textAlign: 'center', padding: '20px', color: '#999', fontSize: '14px' } },
                        type === 'creators' ? "No creators yet" : "No sparks yet"
                    ) :
                    displayData.map((item, index) => {
                        if (type === 'creators') {
                            const isMe = item.id === currentUser?.uid;
                            return React.createElement('div', {
                                key: item.id,
                                style: {
                                    display: 'flex',
                                    alignItems: 'center',
                                    gap: '12px',
                                    padding: '10px',
                                    background: isMe ? 'rgba(139, 92, 246, 0.1)' : 'rgba(255,255,255,0.03)',
                                    borderRadius: '10px',
                                    border: isMe ? '1px solid var(--primary)' : '1px solid transparent'
                                }
                            },
                                React.createElement('div', {
                                    style: {
                                        width: '28px',
                                        height: '28px',
                                        borderRadius: '50%',
                                        background: index < 3 ? getRankColor(index + 1) : 'var(--bg-card-hover)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 'bold',
                                        color: index < 3 ? '#000' : 'var(--text-main)',
                                        fontSize: '13px',
                                        flexShrink: 0
                                    }
                                }, index + 1),
                                React.createElement('div', { style: { flex: 1 } },
                                    React.createElement('button', {
                                        onClick: () => onViewProfile && onViewProfile(item.id),
                                        style: {
                                            fontWeight: 'bold',
                                            fontSize: '14px',
                                            background: 'none',
                                            border: 'none',
                                            cursor: 'pointer',
                                            color: 'var(--text-main)',
                                            textDecoration: 'underline',
                                            textDecorationColor: 'transparent',
                                            transition: 'text-decoration-color 0.2s',
                                            padding: 0,
                                            textAlign: 'left'
                                        },
                                        onMouseEnter: (e) => e.target.style.textDecorationColor = 'var(--primary)',
                                        onMouseLeave: (e) => e.target.style.textDecorationColor = 'transparent'
                                    }, item.name),
                                    React.createElement('div', { style: { fontSize: '11px', color: '#999' } },
                                        item.sparks, " sparks • ", item.totalLikes, " likes"
                                    )
                                ),
                                index < 3 && React.createElement(Award, { size: 16, color: getRankColor(index + 1) })
                            );
                        } else {
                            // Sparks rendering
                            const isMyContent = item.authorId === currentUser?.uid;
                            return React.createElement('div', {
                                key: item.id,
                                style: {
                                    display: 'flex',
                                    gap: '10px',
                                    padding: '10px',
                                    background: isMyContent ? 'rgba(139, 92, 246, 0.1)' : 'rgba(255,255,255,0.03)',
                                    borderRadius: '10px',
                                    border: isMyContent ? '1px solid var(--primary)' : '1px solid transparent'
                                }
                            },
                                React.createElement('div', {
                                    style: {
                                        width: '24px',
                                        height: '24px',
                                        borderRadius: '50%',
                                        background: index < 3 ? getRankColor(index + 1) : 'var(--bg-card-hover)',
                                        display: 'flex',
                                        alignItems: 'center',
                                        justifyContent: 'center',
                                        fontWeight: 'bold',
                                        color: index < 3 ? '#000' : 'var(--text-main)',
                                        fontSize: '11px',
                                        flexShrink: 0
                                    }
                                }, index + 1),
                                React.createElement('div', { style: { flex: 1 } },
                                    React.createElement('div', { style: { fontSize: '12px', color: '#999', marginBottom: '3px' } },
                                        "by ",
                                        React.createElement('button', {
                                            onClick: () => onViewProfile && onViewProfile(item.authorId),
                                            style: {
                                                background: 'none',
                                                border: 'none',
                                                cursor: 'pointer',
                                                color: '#999',
                                                textDecoration: 'underline',
                                                fontSize: '12px',
                                                padding: 0
                                            }
                                        }, item.author)
                                    ),
                                    React.createElement('div', { style: { fontSize: '13px', lineHeight: '1.3' } },
                                        item.content ? item.content.length > 50 ? item.content.substring(0, 50) + '...' : item.content : '(Image post)'
                                    ),
                                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '8px', marginTop: '5px' } },
                                        React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '4px' } },
                                            React.createElement(Heart, { size: 12, fill: '#ec4899', color: '#ec4899' }),
                                            React.createElement('span', { style: { fontSize: '12px', fontWeight: 'bold', color: '#ec4899' } }, item.likes || 0)
                                        ),
                                        item.totalReactions !== undefined && React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '4px' } },
                                            React.createElement(Smile, { size: 12, color: '#a78bfa' }),
                                            React.createElement('span', { style: { fontSize: '12px', fontWeight: 'bold', color: '#a78bfa' } }, item.totalReactions || 0)
                                        )
                                    )
                                )
                            );
                        }
                    })
            ),
            // User rank display (if not in top 3)
            userRank > 3 && React.createElement('div', { style: { marginTop: '12px', padding: '10px', background: 'rgba(139, 92, 246, 0.1)', borderRadius: '8px', border: '1px solid rgba(139, 92, 246, 0.3)', fontSize: '13px', color: '#a78bfa', textAlign: 'center' } },
                "Your Rank: #", userRank, " of ", data.length
            ),
            // Expand/Collapse button
            data.length > 3 && React.createElement('button', {
                onClick: () => togglePanel(panelId),
                style: {
                    marginTop: '15px',
                    width: '100%',
                    padding: '8px',
                    background: 'rgba(255,255,255,0.05)',
                    border: '1px solid rgba(255,255,255,0.1)',
                    borderRadius: '8px',
                    color: '#999',
                    cursor: 'pointer',
                    display: 'flex',
                    alignItems: 'center',
                    justifyContent: 'center',
                    gap: '6px',
                    fontSize: '13px',
                    transition: 'all 0.2s'
                },
                onMouseEnter: (e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.1)';
                    e.currentTarget.style.color = 'white';
                },
                onMouseLeave: (e) => {
                    e.currentTarget.style.background = 'rgba(255,255,255,0.05)';
                    e.currentTarget.style.color = '#999';
                }
            },
                isExpanded ? React.createElement(ChevronUp, { size: 16 }) : React.createElement(ChevronDown, { size: 16 }),
                isExpanded ? 'Show Less' : `View More (${data.length} total)`
            )
        );
    };

    if (loading && !currentUser) return React.createElement('div', { className: 'animate-pulse', style: { padding: '40px', textAlign: 'center' } }, "Loading leaderboard...");

    return React.createElement('div', { className: 'animate-fade-in', style: { maxWidth: '1000px', margin: '0 auto' } },
        // Header with Mascot
        React.createElement('div', { style: { display: 'flex', alignItems: 'center', justifyContent: 'center', gap: '20px', marginBottom: '30px', marginTop: '10px' } },
            React.createElement('img', {
                src: "/images/mascots/SparkStonks.png",
                alt: "SparkStonks Mascot",
                style: { height: '80px', width: 'auto', objectFit: 'contain' },
                onError: (e) => e.target.style.display = 'none'
            }),
            React.createElement('h1', {
                style: {
                    fontSize: '3rem',
                    fontWeight: '800',
                    margin: 0,
                    background: 'linear-gradient(135deg, #fbbf24 0%, #d97706 100%)', // Gold/Amber gradient for leaderboard
                    WebkitBackgroundClip: 'text',
                    WebkitTextFillColor: 'transparent'
                }
            }, "Leaderboard")
        ),

        // User Stats & Badges Section
        currentUser && React.createElement('div', { className: 'glass-panel', style: { padding: '25px', marginBottom: '30px' } },
            React.createElement('div', { style: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '20px' } },
                React.createElement('h2', { style: { fontSize: '24px', display: 'flex', alignItems: 'center', gap: '10px' } },
                    React.createElement('img', { src: currentUser.avatar, style: { width: '40px', height: '40px', borderRadius: '50%' } }),
                    "Your Stats",
                    React.createElement(UserBadge, { user: currentUser, size: 28 })
                ),
                React.createElement('div', { style: { display: 'flex', gap: '20px', color: '#999', fontSize: '14px' } },
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '6px' } },
                        React.createElement(Sparkles, { size: 16, color: '#ef4444' }),
                        React.createElement('span', { style: { color: 'var(--text-primary)', fontWeight: 'bold' } }, Math.max(0, currentUser.stats?.sparksCreated || 0)), " Sparks"
                    ),
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '6px' } },
                        React.createElement(Heart, { size: 16, color: '#ec4899' }),
                        React.createElement('span', { style: { color: 'var(--text-primary)', fontWeight: 'bold' } }, Math.max(0, currentUser.stats?.likesReceived || 0)), " Likes"
                    ),
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '6px' } },
                        React.createElement(Smile, { size: 16, color: '#a78bfa' }),
                        React.createElement('span', { style: { color: 'var(--text-primary)', fontWeight: 'bold' } }, Math.max(0, currentUser.stats?.reactionsReceived || 0)), " Reactions"
                    ),
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '6px' } },
                        React.createElement(Zap, { size: 16, color: '#f59e0b' }),
                        React.createElement('span', { style: { color: 'var(--text-primary)', fontWeight: 'bold' } }, Math.max(0, currentUser.stats?.streak || 0)), " Streak"
                    ),
                    React.createElement('div', { style: { display: 'flex', alignItems: 'center', gap: '6px' } },
                        React.createElement(Trophy, { size: 16, color: '#06b6d4' }),
                        React.createElement('span', { style: { color: 'var(--text-primary)', fontWeight: 'bold' } }, Math.max(0, currentUser.stats?.challengesCompleted || 0)), " Challenges"
                    )
                )
            ),

            // Badges (including Magic Spark if holder)
            React.createElement('h3', { style: { fontSize: '16px', marginBottom: '15px', color: '#999' } }, "Badges"),
            React.createElement('div', { style: { display: 'flex', gap: '15px', flexWrap: 'wrap' } },
                // Regular badges
                userBadges.map(badge => {
                    const IconComponent = iconMap[badge.icon] || Award;
                    return React.createElement('div', {
                        key: badge.id,
                        title: badge.unlocked ? badge.name : "Locked",
                        style: {
                            width: '60px',
                            height: '60px',
                            borderRadius: '12px',
                            background: badge.unlocked ? `rgba(${parseInt(badge.color.slice(1, 3), 16)}, ${parseInt(badge.color.slice(3, 5), 16)}, ${parseInt(badge.color.slice(5, 7), 16)}, 0.1)` : 'rgba(255,255,255,0.03)',
                            border: badge.unlocked ? `1px solid ${badge.color}` : '1px solid transparent',
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'center',
                            justifyContent: 'center',
                            opacity: badge.unlocked ? 1 : 0.3,
                            transition: 'all 0.2s',
                            cursor: 'help'
                        }
                    },
                        React.createElement(IconComponent, { size: 24, color: badge.unlocked ? badge.color : '#999' })
                    );
                }),
                // Magic Spark Badge (only if you're the holder)
                magicSparkHolder === currentUser.name && React.createElement('div', { // Wait, logic: magicSparkHolder is name?? 
                    // firestore.js's getMagicSparkHolder returns { holderName, currentHolder(id) ... }
                    // Logic above: setMagicSparkHolder(magicData?.holderName || null)
                    // So we are comparing user NAME. 
                    // Better to compare IDs if possible.
                    // But mock logic compared name.
                    // Let's stick to name for display match, or check ID if available?
                    // Magic Spark data has ID. User has ID.
                    // Let's rely on name for now as per state.
                    key: 'magic_spark',
                    title: '✨ Magic Spark',
                    className: 'animate-fade-in',
                    style: {
                        width: '60px',
                        height: '60px',
                        borderRadius: '12px',
                        background: 'linear-gradient(135deg, #ff0080, #ff8c00, #40e0d0, #ff0080)',
                        backgroundSize: '300% 300%',
                        animation: 'gradient 3s ease infinite',
                        border: '2px solid rgba(255, 255, 255, 0.3)',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        cursor: 'help',
                        boxShadow: '0 0 20px rgba(255, 0, 128, 0.4)'
                    }
                },
                    React.createElement(Sparkles, { size: 24, color: 'white', fill: 'white' })
                )
            )
        ),

        // Global Leaderboards
        React.createElement('h3', { style: { marginBottom: '15px', fontSize: '18px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px' } }, "Global Rankings"),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px', marginBottom: '30px' } },
            renderLeaderboardPanel("Top Creators", React.createElement(Trophy, { size: 22, color: 'var(--primary)' }), topCreators, 'creators', 'global-creators'),
            renderLeaderboardPanel("Most Liked Sparks", React.createElement(Heart, { size: 22, color: 'var(--secondary)' }), topSparks, 'sparks', 'global-liked'),
            renderLeaderboardPanel("Most Reacted Sparks", React.createElement(Smile, { size: 22, color: '#a78bfa' }), topReactedSparks, 'sparks', 'global-reacted')
        ),

        // Friends Leaderboards
        React.createElement('h3', { style: { marginBottom: '15px', fontSize: '18px', color: '#999', textTransform: 'uppercase', letterSpacing: '1px', display: 'flex', alignItems: 'center', gap: '8px' } },
            React.createElement(Users, { size: 20 }),
            "Friends Rankings"
        ),
        React.createElement('div', { style: { display: 'grid', gridTemplateColumns: '1fr 1fr 1fr', gap: '20px' } },
            renderLeaderboardPanel("Top Creators - Friends", React.createElement(Trophy, { size: 22, color: 'var(--primary)' }), friendTopCreators, 'creators', 'friend-creators'),
            renderLeaderboardPanel("Most Liked Sparks - Friends", React.createElement(Heart, { size: 22, color: 'var(--secondary)' }), friendTopSparks, 'sparks', 'friend-liked'),
            renderLeaderboardPanel("Most Reacted Sparks - Friends", React.createElement(Smile, { size: 22, color: '#a78bfa' }), friendTopReactedSparks, 'sparks', 'friend-reacted')
        )
    );
}
